import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import Button from '../components/ui/button';
import LoginForm from '../components/admin/login-form';
import AnimatedBackground from '../components/ui/animated-background';

// Interface for waitlist entry
interface WaitlistEntry {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  companySize: string;
  taxSoftware: string[];
  numberOfReturns: string;
  cardLastFour: string | null;
  hasPaymentMethod: boolean;
  createdAt: any;
}

const AdminContainer = styled.div`
  padding: 6rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: ${({ theme }) => theme.colors.gradient.text};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const AdminPanel = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.colors.light};
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: ${({ theme }) => theme.colors.lightGray};
  }
`;

const TableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayLight};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.gray};
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  padding: 1rem;
  margin-bottom: 1rem;
  background-color: rgba(255, 0, 0, 0.05);
  border-radius: 4px;
`;

const AdminPage: React.FC = () => {
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    document.title = 'Admin Dashboard | FylePrep';
    
    // Check if user is already authenticated
    const authStatus = sessionStorage.getItem('fyleprep_admin_auth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      fetchWaitlistData();
    }
  }, []);

  const fetchWaitlistData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const waitlistQuery = query(
        collection(db, 'waitlist'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(waitlistQuery);
      
      const entries: WaitlistEntry[] = [];
      snapshot.forEach(doc => {
        const data = doc.data() as Omit<WaitlistEntry, 'id'>;
        entries.push({
          id: doc.id,
          ...data,
          // Format createdAt if it exists
          createdAt: data.createdAt ? new Date(data.createdAt.toDate()).toLocaleString() : 'N/A'
        });
      });
      
      setWaitlistEntries(entries);
    } catch (err) {
      console.error('Error fetching waitlist data:', err);
      setError('Failed to load waitlist data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    // Format data for CSV
    const headers = ['ID', 'First Name', 'Last Name', 'Email', 'Company Name', 
                    'Company Size', 'Tax Software', 'Number of Returns', 
                    'Card Last Four', 'Has Payment Method', 'Created At'];
    
    const csvRows = [
      headers.join(','), // Header row
      ...waitlistEntries.map(entry => {
        // Format each entry as a CSV row
        return [
          entry.id,
          `"${entry.firstName || ''}"`,
          `"${entry.lastName || ''}"`,
          `"${entry.email || ''}"`,
          `"${entry.companyName || ''}"`,
          `"${entry.companySize || ''}"`,
          `"${Array.isArray(entry.taxSoftware) ? entry.taxSoftware.join('; ') : ''}"`,
          `"${entry.numberOfReturns || ''}"`,
          entry.cardLastFour || '',
          entry.hasPaymentMethod ? 'Yes' : 'No',
          `"${entry.createdAt || ''}"`
        ].join(',');
      })
    ];
    
    // Create CSV content
    const csvContent = csvRows.join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    // Create temporary link and trigger download
    const link = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `fyleprep-waitlist-${date}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('fyleprep_admin_auth');
    setIsAuthenticated(false);
  };

  return (
    <AdminContainer>
      <AnimatedBackground />
      
      <PageTitle
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Admin Dashboard
      </PageTitle>
      
      {!isAuthenticated ? (
        <LoginForm onLogin={(success) => {
          if (success) {
            setIsAuthenticated(true);
            fetchWaitlistData();
          }
        }} />
      ) : (
        <AdminPanel>
          <ButtonContainer>
            <Button onClick={fetchWaitlistData} disabled={loading}>
              Refresh Data
            </Button>
            <Button 
              onClick={exportToCSV} 
              disabled={loading || waitlistEntries.length === 0}
              variant="secondary"
            >
              Export to CSV
            </Button>
            <Button onClick={handleLogout} variant="outline">
              Logout
            </Button>
          </ButtonContainer>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          {loading ? (
            <LoadingMessage>Loading waitlist data...</LoadingMessage>
          ) : waitlistEntries.length === 0 ? (
            <LoadingMessage>No waitlist entries found.</LoadingMessage>
          ) : (
            <Table>
              <thead>
                <tr>
                  <TableHeader>Name</TableHeader>
                  <TableHeader>Email</TableHeader>
                  <TableHeader>Company</TableHeader>
                  <TableHeader>Company Size</TableHeader>
                  <TableHeader>Tax Software</TableHeader>
                  <TableHeader>Returns</TableHeader>
                  <TableHeader>Payment</TableHeader>
                  <TableHeader>Date</TableHeader>
                </tr>
              </thead>
              <tbody>
                {waitlistEntries.map(entry => (
                  <TableRow key={entry.id}>
                    <TableCell>{`${entry.firstName} ${entry.lastName}`}</TableCell>
                    <TableCell>{entry.email}</TableCell>
                    <TableCell>{entry.companyName}</TableCell>
                    <TableCell>{entry.companySize}</TableCell>
                    <TableCell>{Array.isArray(entry.taxSoftware) ? entry.taxSoftware.join(', ') : ''}</TableCell>
                    <TableCell>{entry.numberOfReturns}</TableCell>
                    <TableCell>{entry.hasPaymentMethod ? `Yes (${entry.cardLastFour})` : 'No'}</TableCell>
                    <TableCell>{entry.createdAt}</TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </AdminPanel>
      )}
    </AdminContainer>
  );
};

export default AdminPage;
